"use client";

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Database, Play, Loader2, ArrowLeft, BarChart3, PieChart, Save, FileText, MessageSquare, Search, Upload } from "lucide-react";
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { DocumentManager } from "../planning/DocumentManager";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ReviewComment } from "../shared/ReviewComment";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface IAAdditionsDeletionsProps {
  onBack?: () => void;
  workspaceName?: string;
  procedureName?: string;
}

interface ClientFile {
  name: string;
  reference: string;
}

interface ExcelColumn {
  name: string;
  value: string;
}

export default function IAAdditionsDeletions({ onBack, workspaceName, procedureName }: IAAdditionsDeletionsProps) {
  // File search states for Additions
  const [additionsFileSearchStatus, setAdditionsFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundAdditionsFiles, setFoundAdditionsFiles] = useState<string[]>([]);
  const [additionsReferenceValue, setAdditionsReferenceValue] = useState<string>("");
  const [additionsUploadStatus, setAdditionsUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  // File search states for Deletions
  const [deletionsFileSearchStatus, setDeletionsFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundDeletionsFiles, setFoundDeletionsFiles] = useState<string[]>([]);
  const [deletionsReferenceValue, setDeletionsReferenceValue] = useState<string>("");
  const [deletionsUploadStatus, setDeletionsUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  const [additionsFile, setAdditionsFile] = useState<string>("");
  const [deletionsFile, setDeletionsFile] = useState<string>("");

  const [isLoadingAddCols, setIsLoadingAddCols] = useState(false);
  const [isLoadingDelCols, setIsLoadingDelCols] = useState(false);
  const [additionColumns, setAdditionColumns] = useState<ExcelColumn[]>([]);
  const [deletionColumns, setDeletionColumns] = useState<ExcelColumn[]>([]);

  const [additionsDateCol, setAdditionsDateCol] = useState<string>("");
  const [additionsAmountCol, setAdditionsAmountCol] = useState<string>("");
  const [deletionsDateCol, setDeletionsDateCol] = useState<string>("");
  const [deletionsAmountCol, setDeletionsAmountCol] = useState<string>("");

  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `IA_ADDITIONS_DELETIONS_${normalizedSection}`;
  };
  
  // Analysis Results states
  const [tableData, setTableData] = useState<any>(null);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [showAnalysisSelection, setShowAnalysisSelection] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [columnAnalyses, setColumnAnalyses] = useState<Record<string, any>>({});
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [additionsTableData, setAdditionsTableData] = useState<any[]>([]);
  const [deletionsTableData, setDeletionsTableData] = useState<any[]>([]);
  
  // Table response states for saving
  const [additionsTextResponse, setAdditionsTextResponse] = useState<any>(null);
  const [additionsDocuments, setAdditionsDocuments] = useState<any[]>([]);
  const [deletionsTextResponse, setDeletionsTextResponse] = useState<any>(null);
  const [deletionsDocuments, setDeletionsDocuments] = useState<any[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // ===== Composition Analysis (copied from PPE ADL, adapted for Intangibles) =====
  // Composition Analysis states
  const [additionsCompositionColumns, setAdditionsCompositionColumns] = useState<Set<string>>(new Set());
  const [deletionsCompositionColumns, setDeletionsCompositionColumns] = useState<Set<string>>(new Set());
  const [additionsCompositionData, setAdditionsCompositionData] = useState<any[]>([]);
  const [deletionsCompositionData, setDeletionsCompositionData] = useState<any[]>([]);
  const [additionsCompositionResults, setAdditionsCompositionResults] = useState<Record<string, any>>({});
  const [deletionsCompositionResults, setDeletionsCompositionResults] = useState<Record<string, any>>({});
  const [isRunningCompositionAnalysis, setIsRunningCompositionAnalysis] = useState(false);
  const [showAdditionsCompositionDropdown, setShowAdditionsCompositionDropdown] = useState(false);
  const [showDeletionsCompositionDropdown, setShowDeletionsCompositionDropdown] = useState(false);
  const [additionsCompositionColumnsSearch, setAdditionsCompositionColumnsSearch] = useState("");
  const [deletionsCompositionColumnsSearch, setDeletionsCompositionColumnsSearch] = useState("");
  const additionsDropdownRef = useRef<HTMLDivElement>(null);
  const deletionsDropdownRef = useRef<HTMLDivElement>(null);
  const [additionsCompositionAmountCol, setAdditionsCompositionAmountCol] = useState<string>("");
  const [deletionsCompositionAmountCol, setDeletionsCompositionAmountCol] = useState<string>("");
  const [isRunningAdditionsCompositionAnalysis, setIsRunningAdditionsCompositionAnalysis] = useState(false);
  const [isRunningDeletionsCompositionAnalysis, setIsRunningDeletionsCompositionAnalysis] = useState(false);
  const [loadedAdditionsCompositionData, setLoadedAdditionsCompositionData] = useState<Record<string, any>>({});
  const [loadedDeletionsCompositionData, setLoadedDeletionsCompositionData] = useState<Record<string, any>>({});
  const [isLoadingCompositionAnalysis, setIsLoadingCompositionAnalysis] = useState(false);
  const [isLoadingDeletionsAnalysis, setIsLoadingDeletionsAnalysis] = useState(false);

  // State for Composition Analysis textResponse and documents (keyed by column name)
  const [additionsCompositionResponses, setAdditionsCompositionResponses] = useState<Record<string, { textResponse?: any; documents?: any[] }>>({});
  const [deletionsCompositionResponses, setDeletionsCompositionResponses] = useState<Record<string, { textResponse?: any; documents?: any[] }>>({});

  // Save status for each column
  const [additionsSaveStatus, setAdditionsSaveStatus] = useState<Record<string, "idle" | "saving" | "saved" | "error">>({});
  const [deletionsSaveStatus, setDeletionsSaveStatus] = useState<Record<string, "idle" | "saving" | "saved" | "error">>({});

  // Save all composition analysis data
  const [isSavingAllComposition, setIsSavingAllComposition] = useState(false);
  const [saveAllStatus, setSaveAllStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Save Additions Composition Analysis state
  const [isSavingAdditionsComposition, setIsSavingAdditionsComposition] = useState(false);
  const [saveAdditionsCompositionStatus, setSaveAdditionsCompositionStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveAdditionsCompositionError, setSaveAdditionsCompositionError] = useState<string | null>(null);

  // Save Deletions Composition Analysis state
  const [isSavingDeletionsComposition, setIsSavingDeletionsComposition] = useState(false);
  const [saveDeletionsCompositionStatus, setSaveDeletionsCompositionStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveDeletionsCompositionError, setSaveDeletionsCompositionError] = useState<string | null>(null);

  // Debug state for troubleshooting
  const [debugInfo, setDebugInfo] = useState<{
    additions?: {
      step?: string;
      stdout?: string;
      parsed?: any;
      chartData?: any;
      error?: string;
      details?: string;
    };
    deletions?: {
      step?: string;
      stdout?: string;
      parsed?: any;
      chartData?: any;
      error?: string;
      details?: string;
    };
    compAnal?: {
      step?: string;
      stdout?: string;
      parsed?: any;
      data?: any;
      error?: string;
      details?: string;
    };
  }>({});

  // Auto-save debounce refs
  const additionsSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const deletionsSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flag to prevent auto-save during initial load
  const isInitialLoadRef = useRef(true);

  // Flag to prevent auto-save during manual save operation
  const isManualSaveInProgressRef = useRef(false);

  // Debug: Log when loaded data changes
  useEffect(() => {
    const additionsKeys = Object.keys(loadedAdditionsCompositionData);
    const deletionsKeys = Object.keys(loadedDeletionsCompositionData);
    if (additionsKeys.length > 0 || deletionsKeys.length > 0) {
      setDebugInfo(prev => ({
        ...prev,
        additions: {
          ...prev.additions,
          step: prev.additions?.step || "State Updated",
          details: `State has ${additionsKeys.length} additions keys: ${additionsKeys.join(', ')}`
        },
        deletions: {
          ...prev.deletions,
          step: prev.deletions?.step || "State Updated",
          details: `State has ${deletionsKeys.length} deletions keys: ${deletionsKeys.join(', ')}`
        }
      }));
    }
  }, [loadedAdditionsCompositionData, loadedDeletionsCompositionData]);

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Intangible Assets", "Additions & Deletions Analysis");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  useEffect(() => {
    searchForAdditionsFiles();
    searchForDeletionsFiles();
    downloadAndDisplayTable();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_IT_AL_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const fileName = `Execution_IT_AL_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "IA-ADL-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  const searchForAdditionsFiles = async () => {
    setAdditionsFileSearchStatus("searching");
    console.log(" Starting file search for Additions Listing...");
    
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_IT_AL");
        console.log(" SharePoint API result for Additions:", result);
        
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          
          if (files.length > 0) {
            console.log(" Additions files found:", files);
            setFoundAdditionsFiles(files);
            setAdditionsFileSearchStatus("found");
          } else {
            console.log(" No additions files found");
            setAdditionsFileSearchStatus("not_found");
          }
        } else {
          console.log(" API call failed:", result.error);
          setAdditionsFileSearchStatus("not_found");
        }
      } else {
        console.error("SharePoint API not available");
        setAdditionsFileSearchStatus("not_found");
      }
    } catch (e) {
      console.error("Error searching for Additions files:", e);
      setAdditionsFileSearchStatus("not_found");
    }
  };

  const searchForDeletionsFiles = async () => {
    setDeletionsFileSearchStatus("searching");
    console.log(" Starting file search for Deletions Listing...");
    
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_IT_DL");
        console.log(" SharePoint API result for Deletions:", result);
        
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          
          if (files.length > 0) {
            console.log(" Deletions files found:", files);
            setFoundDeletionsFiles(files);
            setDeletionsFileSearchStatus("found");
          } else {
            console.log(" No deletions files found");
            setDeletionsFileSearchStatus("not_found");
          }
        } else {
          console.log(" API call failed:", result.error);
          setDeletionsFileSearchStatus("not_found");
        }
      } else {
        console.error("SharePoint API not available");
        setDeletionsFileSearchStatus("not_found");
      }
    } catch (e) {
      console.error("Error searching for Deletions files:", e);
      setDeletionsFileSearchStatus("not_found");
    }
  };

  const uploadAdditionsFile = async () => {
    setAdditionsUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: additionsReferenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_IT_AL"]
              });

              if (result.success) {
                console.log(" Additions file uploaded successfully:", file.name);
                setAdditionsFile(file.name);
                setFoundAdditionsFiles([file.name]);
                setAdditionsFileSearchStatus("found");
                setAdditionsUploadStatus("completed");
              } else {
                console.error(" Upload failed:", result.error);
                setAdditionsUploadStatus("error");
              }
            } else {
              console.error("SharePoint API not available");
              setAdditionsUploadStatus("error");
            }
          } catch (error) {
            console.error("Error uploading file:", error);
            setAdditionsUploadStatus("error");
          }
        }
      };
      input.click();
    } catch (error) {
      console.error("Error in uploadAdditionsFile:", error);
      setAdditionsUploadStatus("error");
    }
  };

  const uploadDeletionsFile = async () => {
    setDeletionsUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: deletionsReferenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_IT_DL"]
              });

              if (result.success) {
                console.log(" Deletions file uploaded successfully:", file.name);
                setDeletionsFile(file.name);
                setFoundDeletionsFiles([file.name]);
                setDeletionsFileSearchStatus("found");
                setDeletionsUploadStatus("completed");
              } else {
                console.error(" Upload failed:", result.error);
                setDeletionsUploadStatus("error");
              }
            } else {
              console.error("SharePoint API not available");
              setDeletionsUploadStatus("error");
            }
          } catch (error) {
            console.error("Error uploading file:", error);
            setDeletionsUploadStatus("error");
          }
        }
      };
      input.click();
    } catch (error) {
      console.error("Error in uploadDeletionsFile:", error);
      setDeletionsUploadStatus("error");
    }
  };

  const loadAdditionColumns = async () => {
    if (!additionsFile) return;
    setIsLoadingAddCols(true);
    try {
      if ((window as any).payroll?.loadExcelColumns) {
        const result = await (window as any).payroll.loadExcelColumns(additionsFile);
        if (result.ok && Array.isArray(result.columns)) {
          setAdditionColumns(result.columns.map((c: any) => ({ name: c, value: c })));
          // Save file name to file usage reference
          const fileName = additionsFile.includes(' (') ? additionsFile.split(' (')[0] : additionsFile;
          await updateProcedureFileUsageForWorkspace("Intangible Assets", "Additions & Deletions Analysis", [{ 
            "Search for files - Additions": fileName 
          }]);
          await loadFileUsageReference();
        }
      }
    } finally {
      setIsLoadingAddCols(false);
    }
  };

  const loadDeletionColumns = async () => {
    if (!deletionsFile) return;
    setIsLoadingDelCols(true);
    try {
      if ((window as any).payroll?.loadExcelColumns) {
        const result = await (window as any).payroll.loadExcelColumns(deletionsFile);
        if (result.ok && Array.isArray(result.columns)) {
          setDeletionColumns(result.columns.map((c: any) => ({ name: c, value: c })));
          // Save file name to file usage reference
          const fileName = deletionsFile.includes(' (') ? deletionsFile.split(' (')[0] : deletionsFile;
          await updateProcedureFileUsageForWorkspace("Intangible Assets", "Additions & Deletions Analysis", [{ 
            "Search for files - Deletions": fileName 
          }]);
          await loadFileUsageReference();
        }
      }
    } finally {
      setIsLoadingDelCols(false);
    }
  };

  // Handle composition column selection (Additions)
  const handleAdditionsCompositionColumnToggle = (column: string) => {
    setAdditionsCompositionColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  // Handle composition column selection (Deletions)
  const handleDeletionsCompositionColumnToggle = (column: string) => {
    setDeletionsCompositionColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  const canRun = () =>
    additionsFile && deletionsFile && additionsDateCol && additionsAmountCol && deletionsDateCol && deletionsAmountCol;

  const runADL = async () => {
    if (!canRun()) return;
    
    // Save column mappings to file usage reference
    const entries: FileUsageEntry[] = [];
    const additionsFileName = additionsFile.includes(' (') ? additionsFile.split(' (')[0] : additionsFile;
    const deletionsFileName = deletionsFile.includes(' (') ? deletionsFile.split(' (')[0] : deletionsFile;
    entries.push({ "Search for files - Additions": additionsFileName });
    entries.push({ "Search for files - Deletions": deletionsFileName });
    const columnMapping: Record<string, any> = {
      additions: {
        dateColumn: additionsDateCol,
        amountColumn: additionsAmountCol
      },
      deletions: {
        dateColumn: deletionsDateCol,
        amountColumn: deletionsAmountCol
      }
    };
    entries.push({ "Column Mapping": JSON.stringify(columnMapping) });
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Intangible Assets", "Additions & Deletions Analysis", entries);
      await loadFileUsageReference();
    }
    
    setProcessingStatus("running");
    setFileUrl("");

    try {
      if (window.payroll?.run) {
        // Strip whitespace from column names before passing to Python
        const colAdd = [additionsDateCol.trim(), additionsAmountCol.trim()];
        const colDel = [deletionsDateCol.trim(), deletionsAmountCol.trim()];
        
        const result = await window.payroll.run("execute_ia_adl", {
          inputFiles: [],
          options: {
            file_additions: additionsFile,
            file_deletions: deletionsFile,
            col_add: colAdd,
            col_del: colDel,
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
          try {
            if (result.stdout) {
              const lines = String(result.stdout).split("\n");
              const jsonLine = lines.find((l: string) => l.trim().startsWith("{") && l.trim().endsWith("}"));
              if (jsonLine) {
                const parsed = JSON.parse(jsonLine);
                if (parsed.json_url) setFileUrl(parsed.json_url);
              }
            }
          } catch {}
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (error) {
      setProcessingStatus("error");
    }
  };

  const generateDummyTableData = (columns: ExcelColumn[], count: number = 10) => {
    return Array.from({ length: count }, (_, idx) => {
      const row: any = {};
      columns.forEach(col => {
        const colName = col.name.toLowerCase();
        if (colName.includes('date')) {
          row[col.name] = new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString();
        } else if (colName.includes('amount') || colName.includes('cost') || colName.includes('value')) {
          row[col.name] = (Math.random() * 1000000).toFixed(2);
        } else if (colName.includes('code') || colName.includes('id')) {
          row[col.name] = `CODE-${String(idx + 1).padStart(4, '0')}`;
        } else if (colName.includes('location')) {
          const locations = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad'];
          row[col.name] = locations[Math.floor(Math.random() * locations.length)];
        } else if (colName.includes('category') || colName.includes('type')) {
          const categories = ['Patent', 'Trademark', 'Copyright', 'Software', 'License'];
          row[col.name] = categories[Math.floor(Math.random() * categories.length)];
        } else {
          row[col.name] = `Sample ${col.name} ${idx + 1}`;
        }
      });
      return row;
    });
  };

  // ===== Composition Analysis helpers & load functions (IT, mirroring PPE) =====

  const runAdditionsCompositionAnalysis = async () => {
    if (additionsCompositionColumns.size === 0 || !additionsCompositionAmountCol) return;

    setIsRunningAdditionsCompositionAnalysis(true);
    try {
      if ((window as any).payroll?.run) {
        const result = await (window as any).payroll.run("execute_it_additions_composition_analysis", {
          inputFiles: [],
          options: {
            group_columns: Array.from(additionsCompositionColumns),
            amount_column: additionsCompositionAmountCol,
          },
        });

        setIsRunningAdditionsCompositionAnalysis(false);
      } else {
        setIsRunningAdditionsCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running IT additions composition analysis:", error);
      setIsRunningAdditionsCompositionAnalysis(false);
    }
  };

  const runDeletionsCompositionAnalysis = async () => {
    if (deletionsCompositionColumns.size === 0 || !deletionsCompositionAmountCol) return;

    setIsRunningDeletionsCompositionAnalysis(true);
    try {
      if ((window as any).payroll?.run) {
        const result = await (window as any).payroll.run("execute_it_deletions_composition_analysis", {
          inputFiles: [],
          options: {
            group_columns: Array.from(deletionsCompositionColumns),
            amount_column: deletionsCompositionAmountCol,
          },
        });

        setIsRunningDeletionsCompositionAnalysis(false);
      } else {
        setIsRunningDeletionsCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running IT deletions composition analysis:", error);
      setIsRunningDeletionsCompositionAnalysis(false);
    }
  };

  const extractJSONFromStdout = (stdout: string): any | null => {
    try {
      const normalized = stdout.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      const lines = normalized.split("\n");

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("{") && trimmed.includes('"success"') && trimmed.includes('"data"')) {
          try {
            const cleanLine = trimmed.replace(/\r$/, "").trim();
            const parsed = JSON.parse(cleanLine);
            if (parsed.success && parsed.data) {
              return parsed.data;
            }
          } catch {
            try {
              const jsonMatch = trimmed.match(/\{.*"success".*"data".*\}/);
              if (jsonMatch) {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.success && parsed.data) {
                  return parsed.data;
                }
              }
            } catch {
              // ignore
            }
          }
        }
      }
    } catch (e) {
      console.error("Error extracting JSON from stdout (IT comp):", e);
    }
    return null;
  };

  const loadCompositionAnalysis = async () => {
    console.log(" loadCompositionAnalysis (IT Additions) called");
    setIsLoadingCompositionAnalysis(true);
    setDebugInfo(prev => ({ ...prev, additions: { step: "Starting", details: "Function called (IT)" } }));

    try {
      if (!(window as any).payroll?.run) {
        setDebugInfo(prev => ({ ...prev, additions: { step: "Error", error: "window.payroll.run not available" } }));
        setIsLoadingCompositionAnalysis(false);
        return;
      }

      // Step 1: Load chart data from Execution_IT_ALCompAnls.json
      setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Calling download_it_alcomp_anls script", details: "Waiting for script to start..." } }));
      let chartData: Record<string, any> | null = null;
      let debugStdout = "";
      let debugParsed: any = null;
      let debugError: string | undefined = undefined;
      
      try {
        // Set up listener BEFORE calling run() to catch early payloads
        let allPayloads: any[] = [];
        let payloadCount = 0;
        let listenerRunId: string | null = null;
        
        // Set up a temporary listener to catch ALL payloads
        const tempUnsubscribe = (window as any).payroll.onProgress((payload: any) => {
          payloadCount++;
          allPayloads.push({ ...payload, receivedAt: new Date().toISOString() });
          setDebugInfo(prev => ({ 
            ...prev, 
            additions: { 
              step: `Step 1: Global payload #${payloadCount} received`, 
              details: `RunId: "${payload.runId}", Status: ${payload.status || 'none'}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` 
            } 
          }));
        });
        
        const chartResult = await (window as any).payroll.run("download_it_alcomp_anls", {
          inputFiles: [],
          options: {},
        });
        
        // Log full result object for debugging
        const resultKeys = Object.keys(chartResult || {});
        const resultString = JSON.stringify(chartResult).substring(0, 500);
        listenerRunId = String(chartResult?.runId || '');
        setDebugInfo(prev => ({ 
          ...prev, 
          additions: { 
            step: "Step 1: Script called", 
            details: `Result: ok=${chartResult?.ok}, runId=${listenerRunId}, error=${chartResult?.error || 'none'}, keys: ${resultKeys.join(', ')}, result: ${resultString}, Global payloads so far: ${allPayloads.length}` 
          } 
        }));

        if (chartResult.ok && chartResult.runId) {
          // Now set up specific listener for this runId
          const expectedRunId = String(chartResult.runId);
          let allStdout = "";
          let statusReceived = false;
          let finalPayload: any = null;
          
          setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Listening for payloads", details: `Expected runId: ${expectedRunId}, runId type: ${typeof chartResult.runId}, Global payloads before: ${allPayloads.length}` } }));
          
          // Check if we already received payloads in the global listener
          const matchingPayloads = allPayloads.filter(p => String(p.runId || '') === expectedRunId);
          if (matchingPayloads.length > 0) {
            setDebugInfo(prev => ({ 
              ...prev, 
              additions: { 
                step: "Step 1: Found matching payloads in global listener", 
                details: `Found ${matchingPayloads.length} matching payload(s). Processing them...` 
              } 
            }));
            
            // Process the matching payloads we already received
            for (const payload of matchingPayloads) {
              finalPayload = payload;
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
                const extracted = extractJSONFromStdout(allStdout);
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  debugParsed = extracted;
                }
              }
              if (payload.status === "success") {
                statusReceived = true;
              }
            }
          }
          
          // Continue listening for more payloads
          const unsubscribe = (window as any).payroll.onProgress((payload: any) => {
            const payloadRunId = String(payload.runId || '');
            const matches = payloadRunId === expectedRunId;
            
            setDebugInfo(prev => ({ 
              ...prev, 
              additions: { 
                step: `Step 1: Payload received (specific listener)`, 
                details: `Payload runId: "${payloadRunId}", Expected: "${expectedRunId}", Match: ${matches}, Status: ${payload.status || 'none'}, Has stdout: ${!!payload.stdout}` 
              } 
            }));
            
            if (!matches) {
              return;
            }
            
            finalPayload = { ...payload, receivedAt: new Date().toISOString() };
            setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Payload received", details: `Status: ${payload.status}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` } }));
            console.log(" Payload matched! Status:", payload.status, "Has stdout:", !!payload.stdout, "Stdout length:", payload.stdout ? String(payload.stdout).length : 0);
            
            // Accumulate stdout from any payload that has it
            if (payload.stdout) {
              allStdout = String(payload.stdout); // Use the full accumulated stdout from payload
              debugStdout = allStdout;
              setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Processing stdout", details: `Stdout length: ${allStdout.length}` } }));
              console.log(" Got stdout, length:", allStdout.length);
              try {
                const extracted = extractJSONFromStdout(allStdout);
                debugParsed = extracted;
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Data extracted", details: `Chart data keys: ${Object.keys(extracted).join(', ')}` } }));
                  console.log(" Extracted additions chart data with keys:", Object.keys(extracted));
                } else {
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Extraction failed", details: "Extracted data is empty or invalid" } }));
                }
              } catch (e) {
                debugError = `Error extracting JSON: ${e}`;
                setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: JSON parse error", details: String(e) } }));
                console.error(" Error extracting JSON:", e);
              }
            }
        
            // Check for success status
            if (payload.status === "success") {
              setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Success status received", details: "Script completed successfully" } }));
              console.log(" Success status received!");
              statusReceived = true;
              // Use the stdout from this payload (it should have the full accumulated stdout)
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
              }
              // Try to extract data one more time with the final stdout
              if (!chartData && allStdout) {
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  debugParsed = extracted;
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                    setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Data extracted on success", details: `Chart data keys: ${Object.keys(extracted).join(', ')}` } }));
                    console.log(" Extracted additions chart data on success with keys:", Object.keys(extracted));
                  }
                } catch (e) {
                  debugError = `Error extracting JSON on success: ${e}`;
                  console.error(" Error extracting JSON on success:", e);
                }
              }
            }
            
            // Check for error status
            if (payload.status === "error" || payload.error) {
              debugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
              statusReceived = true; // Mark as received so we exit
              console.error(" Error status received:", debugError);
            }
          });
        
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                setDebugInfo(prev => ({ 
                  ...prev, 
                  additions: { 
                    step: "Step 1: Status received", 
                    details: `Resolving promise. Payload count: ${payloadCount}, All payloads: ${JSON.stringify(allPayloads.map(p => ({ runId: p.runId, status: p.status, hasStdout: !!p.stdout })))}` 
                  } 
                }));
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 100);
            setTimeout(() => {
              const allPayloadsSummary = allPayloads.map(p => `{runId: "${p.runId}", status: "${p.status}", hasStdout: ${!!p.stdout}}`).join(', ');
              setDebugInfo(prev => ({ 
                ...prev, 
                additions: { 
                  step: "Step 1: Timeout reached", 
                  details: `Status received: ${statusReceived}, Payload count: ${payloadCount}, All payloads: [${allPayloadsSummary}], Final payload: ${finalPayload ? JSON.stringify(finalPayload).substring(0, 200) : 'none'}` 
                } 
              }));
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                // If we have data but no status, that's okay - use the data
                if (chartData && Object.keys(chartData).length > 0) {
                  statusReceived = true; // Mark as received so we use the data
                  const keyCount = Object.keys(chartData).length;
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Timeout but data available", details: `Using chart data with ${keyCount} keys` } }));
                } else {
                  debugError = `Timeout waiting for script completion. Received ${payloadCount} payload(s). Total payloads seen: ${allPayloads.length}. All payload runIds: ${allPayloads.map(p => p.runId).join(', ')}`;
                }
              }
              resolve();
            }, 30000); // Increased timeout to 30 seconds
          });
          
          // Clean up temp listener
          tempUnsubscribe();
          
          console.log(" Finished waiting. Chart data:", chartData && Object.keys(chartData).length > 0 ? Object.keys(chartData) : null, "Status received:", statusReceived);
          
          // Fallback: If we didn't get any matching payloads, check all payloads we received
          if (!chartData && allPayloads.length > 0) {
            setDebugInfo(prev => ({ 
              ...prev, 
              additions: { 
                step: "Step 1: Fallback - Checking all received payloads", 
                details: `Total payloads received: ${allPayloads.length}. RunIds: ${allPayloads.map(p => p.runId).join(', ')}. Checking for any with stdout...` 
              } 
            }));
            
            // Check all payloads for stdout, regardless of runId
            for (const payload of allPayloads) {
              if (payload.stdout) {
                const fallbackStdout = String(payload.stdout);
                const extracted = extractJSONFromStdout(fallbackStdout);
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  debugStdout = fallbackStdout;
                  debugParsed = extracted;
                  setDebugInfo(prev => ({ 
                    ...prev, 
                    additions: { 
                      step: "Step 1: Fallback - Data extracted from any payload", 
                      details: `Found data in payload with runId: "${payload.runId}". Chart data keys: ${Object.keys(extracted).join(', ')}` 
                    } 
                  }));
                  break;
                }
              }
            }
          }
        } else {
          tempUnsubscribe();
          debugError = `Script run failed: ${chartResult.error || 'Unknown error'}`;
        }
      } catch (error) {
        debugError = `Exception: ${error instanceof Error ? error.message : String(error)}`;
        console.error("Error loading chart data:", error);
      }

      // Step 2: Store chart data FIRST (before updating debug info)
      setDebugInfo(prev => ({ ...prev, additions: { step: "Step 2: Storing chart data", details: chartData ? `Data available with ${Object.keys(chartData).length} keys: ${Object.keys(chartData).join(', ')}` : "No chart data available" } }));
      if (chartData && Object.keys(chartData).length > 0) {
        const clonedChartData = JSON.parse(JSON.stringify(chartData));
        const dataKeys = Object.keys(clonedChartData);
        const firstKey = dataKeys[0];
        const firstValue = clonedChartData[firstKey];
        const firstValueType = Array.isArray(firstValue) ? 'array' : typeof firstValue;
        const firstValueLength = Array.isArray(firstValue) ? firstValue.length : 'N/A';
        const firstValueSample = Array.isArray(firstValue) && firstValue.length > 0 ? JSON.stringify(firstValue[0]).substring(0, 100) : JSON.stringify(firstValue).substring(0, 100);
        
        setDebugInfo(prev => ({ 
          ...prev, 
          additions: { 
            ...prev.additions,
            step: "Step 2: Chart data stored", 
            details: `Stored ${dataKeys.length} chart columns. First key: "${firstKey}", type: ${firstValueType}, length: ${firstValueLength}, sample: ${firstValueSample}` 
          } 
        }));
        
        setLoadedAdditionsCompositionData(clonedChartData);
      } else {
        setDebugInfo(prev => ({ ...prev, additions: { step: "Step 2: No chart data", details: debugError || `No data extracted from script. chartData: ${chartData ? JSON.stringify(chartData).substring(0, 200) : 'null'}` } }));
      }

      // Update debug info - ALWAYS update, even if empty
      setDebugInfo(prev => ({
        ...prev,
        additions: {
          step: prev.additions?.step || "Unknown",
          details: prev.additions?.details || "",
          stdout: debugStdout || "",
          parsed: debugParsed || null,
          chartData: chartData || null,
          error: debugError || undefined
        }
      }));
    } catch (error) {
      console.error("Error loading additions analysis (IT):", error);
      setDebugInfo(prev => ({
        ...prev,
        additions: {
          ...prev.additions,
          error: error instanceof Error ? error.message : String(error),
        },
      }));
    } finally {
      setIsLoadingCompositionAnalysis(false);
    }
  };

  const loadDeletionsCompositionAnalysis = async () => {
    console.log(" loadDeletionsCompositionAnalysis (IT) called");
    setIsLoadingDeletionsAnalysis(true);
    setDebugInfo(prev => ({ ...prev, deletions: { step: "Starting", details: "Function called (IT)" } }));

    try {
      if (!(window as any).payroll?.run) {
        setDebugInfo(prev => ({ ...prev, deletions: { step: "Error", error: "window.payroll.run not available" } }));
        setIsLoadingDeletionsAnalysis(false);
        return;
      }

      // Step 1: Load chart data from Execution_IT_DLCompAnls.json
      setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Calling download_it_dlcomp_anls script", details: "Waiting for script to start..." } }));
      let chartData: Record<string, any> | null = null;
      let debugStdout = "";
      let debugParsed: any = null;
      let debugError: string | undefined = undefined;
      
      try {
        // Set up listener BEFORE calling run() to catch early payloads
        let allPayloads: any[] = [];
        let payloadCount = 0;
        let listenerRunId: string | null = null;
        
        // Set up a temporary listener to catch ALL payloads
        const tempUnsubscribe = (window as any).payroll.onProgress((payload: any) => {
          payloadCount++;
          allPayloads.push({ ...payload, receivedAt: new Date().toISOString() });
          setDebugInfo(prev => ({ 
            ...prev, 
            deletions: { 
              step: `Step 1: Global payload #${payloadCount} received`, 
              details: `RunId: "${payload.runId}", Status: ${payload.status || 'none'}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` 
            } 
          }));
        });
        
        const chartResult = await (window as any).payroll.run("download_it_dlcomp_anls", {
          inputFiles: [],
          options: {},
        });
        
        // Log full result object for debugging
        const resultKeys = Object.keys(chartResult || {});
        const resultString = JSON.stringify(chartResult).substring(0, 500);
        listenerRunId = String(chartResult?.runId || '');
        setDebugInfo(prev => ({ 
          ...prev, 
          deletions: { 
            step: "Step 1: Script called", 
            details: `Result: ok=${chartResult?.ok}, runId=${listenerRunId}, error=${chartResult?.error || 'none'}, keys: ${resultKeys.join(', ')}, result: ${resultString}, Global payloads so far: ${allPayloads.length}` 
          } 
        }));

        if (chartResult.ok && chartResult.runId) {
          let allStdout = "";
          let statusReceived = false;
          let finalPayload: any = null;
          const expectedRunId = String(chartResult.runId);
          
          setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Listening for payloads", details: `Expected runId: ${expectedRunId}, runId type: ${typeof chartResult.runId}, Global payloads before: ${allPayloads.length}` } }));
          
          // Check if we already received payloads in the global listener
          const matchingPayloads = allPayloads.filter(p => String(p.runId || '') === expectedRunId);
          if (matchingPayloads.length > 0) {
            setDebugInfo(prev => ({ 
              ...prev, 
              deletions: { 
                step: "Step 1: Found matching payloads in global listener", 
                details: `Found ${matchingPayloads.length} matching payload(s). Processing them...` 
              } 
            }));
            
            // Process the matching payloads we already received
            for (const payload of matchingPayloads) {
              finalPayload = payload;
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
                const extracted = extractJSONFromStdout(allStdout);
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  debugParsed = extracted;
                }
              }
              if (payload.status === "success") {
                statusReceived = true;
              }
            }
          }
          
          // Continue listening for more payloads
          const unsubscribe = (window as any).payroll.onProgress((payload: any) => {
            const payloadRunId = String(payload.runId || '');
            const matches = payloadRunId === expectedRunId;
            
            setDebugInfo(prev => ({ 
              ...prev, 
              deletions: { 
                step: `Step 1: Payload received (specific listener)`, 
                details: `Payload runId: "${payloadRunId}", Expected: "${expectedRunId}", Match: ${matches}, Status: ${payload.status || 'none'}, Has stdout: ${!!payload.stdout}` 
              } 
            }));
            
            if (!matches) {
              return;
            }

            setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Payload received", details: `Status: ${payload.status}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` } }));

            // Capture all payloads for debugging
            finalPayload = { ...payload, receivedAt: new Date().toISOString() };
            console.log(" Payload matched! Status:", payload.status, "Has stdout:", !!payload.stdout, "Stdout length:", payload.stdout ? String(payload.stdout).length : 0);
            
            // Accumulate stdout from any payload that has it
            if (payload.stdout) {
              allStdout = String(payload.stdout); // Use the full accumulated stdout from payload
              debugStdout = allStdout;
              console.log(" Got stdout, length:", allStdout.length);
              try {
                const extracted = extractJSONFromStdout(allStdout);
                debugParsed = extracted;
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  console.log(" Extracted deletions chart data with keys:", Object.keys(extracted));
                }
              } catch (e) {
                debugError = `Error extracting JSON: ${e}`;
                console.error(" Error extracting JSON:", e);
              }
            }

            // Check for success status
            if (payload.status === "success") {
              console.log(" Success status received!");
              statusReceived = true;
              // Use the stdout from this payload (it should have the full accumulated stdout)
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
              }
              // Try to extract data one more time with the final stdout
              if (!chartData && allStdout) {
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  debugParsed = extracted;
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                    console.log(" Extracted deletions chart data on success with keys:", Object.keys(extracted));
                  }
                } catch (e) {
                  debugError = `Error extracting JSON on success: ${e}`;
                  console.error(" Error extracting JSON on success:", e);
                }
              }
            }
            
            // Check for error status
            if (payload.status === "error" || payload.error) {
              debugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
              statusReceived = true; // Mark as received so we exit
              console.error(" Error status received:", debugError);
            }
          });
          
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                setDebugInfo(prev => ({ 
                  ...prev, 
                  deletions: { 
                    step: "Step 1: Status received", 
                    details: `Resolving promise. Payload count: ${payloadCount}, All payloads: ${JSON.stringify(allPayloads.map(p => ({ runId: p.runId, status: p.status, hasStdout: !!p.stdout })))}` 
                  } 
                }));
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 100);
            setTimeout(() => {
              const allPayloadsSummary = allPayloads.map(p => `{runId: "${p.runId}", status: "${p.status}", hasStdout: ${!!p.stdout}}`).join(', ');
              setDebugInfo(prev => ({ 
                ...prev, 
                deletions: { 
                  step: "Step 1: Timeout reached", 
                  details: `Status received: ${statusReceived}, Payload count: ${payloadCount}, All payloads: [${allPayloadsSummary}], Final payload: ${finalPayload ? JSON.stringify(finalPayload).substring(0, 200) : 'none'}` 
                } 
              }));
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                // If we have data but no status, that's okay - use the data
                if (chartData && Object.keys(chartData).length > 0) {
                  statusReceived = true;
                  const keyCount = Object.keys(chartData).length;
                  setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Timeout but data available", details: `Using chart data with ${keyCount} keys` } }));
                } else {
                  debugError = `Timeout waiting for script completion. Received ${payloadCount} payload(s). Total payloads seen: ${allPayloads.length}. All payload runIds: ${allPayloads.map(p => p.runId).join(', ')}`;
                }
              }
              resolve();
            }, 30000); // Increased timeout to 30 seconds
          });
          
          // Clean up temp listener
          tempUnsubscribe();
          
          console.log(" Finished waiting. Chart data:", chartData ? Object.keys(chartData) : null, "Status received:", statusReceived);
          
          // Fallback: If we didn't get any matching payloads, check all payloads we received
          if (!chartData && allPayloads.length > 0) {
            setDebugInfo(prev => ({ 
              ...prev, 
              deletions: { 
                step: "Step 1: Fallback - Checking all received payloads", 
                details: `Total payloads received: ${allPayloads.length}. RunIds: ${allPayloads.map(p => p.runId).join(', ')}. Checking for any with stdout...` 
              } 
            }));
            
            // Check all payloads for stdout, regardless of runId
            for (const payload of allPayloads) {
              if (payload.stdout) {
                const fallbackStdout = String(payload.stdout);
                const extracted = extractJSONFromStdout(fallbackStdout);
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  debugStdout = fallbackStdout;
                  debugParsed = extracted;
                  setDebugInfo(prev => ({ 
                    ...prev, 
                    deletions: { 
                      step: "Step 1: Fallback - Data extracted from any payload", 
                      details: `Found data in payload with runId: "${payload.runId}". Chart data keys: ${Object.keys(extracted).join(', ')}` 
                    } 
                  }));
                  break;
                }
              }
            }
          }
        } else {
          tempUnsubscribe();
          debugError = `Script run failed: ${chartResult.error || 'Unknown error'}`;
        }
      } catch (error) {
        debugError = `Exception: ${error instanceof Error ? error.message : String(error)}`;
        console.error("Error loading deletions chart data:", error);
      }

      // Step 2: Store chart data FIRST (before updating debug info)
      setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 2: Storing chart data", details: chartData ? `Data available with ${Object.keys(chartData).length} keys: ${Object.keys(chartData).join(', ')}` : "No chart data available" } }));
      if (chartData && Object.keys(chartData).length > 0) {
        const clonedChartData = JSON.parse(JSON.stringify(chartData));
        const dataKeys = Object.keys(clonedChartData);
        const firstKey = dataKeys[0];
        const firstValue = clonedChartData[firstKey];
        const firstValueType = Array.isArray(firstValue) ? 'array' : typeof firstValue;
        const firstValueLength = Array.isArray(firstValue) ? firstValue.length : 'N/A';
        const firstValueSample = Array.isArray(firstValue) && firstValue.length > 0 ? JSON.stringify(firstValue[0]).substring(0, 100) : JSON.stringify(firstValue).substring(0, 100);
        
        setDebugInfo(prev => ({ 
          ...prev, 
          deletions: { 
            ...prev.deletions,
            step: "Step 2: Chart data stored", 
            details: `Stored ${dataKeys.length} chart columns. First key: "${firstKey}", type: ${firstValueType}, length: ${firstValueLength}, sample: ${firstValueSample}` 
          } 
        }));
        
        setLoadedDeletionsCompositionData(clonedChartData);
      } else {
        setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 2: No chart data", details: debugError || `No data extracted from script. chartData: ${chartData ? JSON.stringify(chartData).substring(0, 200) : 'null'}` } }));
      }

      // Update debug info - ALWAYS update, even if empty
      setDebugInfo(prev => ({
        ...prev,
        deletions: {
          step: prev.deletions?.step || "Unknown",
          details: prev.deletions?.details || "",
          stdout: debugStdout || "",
          parsed: debugParsed || null,
          chartData: chartData || null,
          error: debugError || undefined
        }
      }));
    } catch (error) {
      console.error("Error loading deletions analysis (IT):", error);
      setDebugInfo(prev => ({
        ...prev,
        deletions: {
          ...prev.deletions,
          error: error instanceof Error ? error.message : String(error),
        },
      }));
    } finally {
      setIsLoadingDeletionsAnalysis(false);
    }
  };

  // Save all composition analysis data to Execution_IT_Substantive.json
  const saveAllCompositionAnalysis = useCallback(async () => {
    // Prevent multiple simultaneous saves
    if (isManualSaveInProgressRef.current) {
      console.log("Save already in progress, skipping...");
      return;
    }
    
    // Set flag FIRST before any operations that might trigger state updates
    isManualSaveInProgressRef.current = true;
    
    // Clear any pending auto-save timeouts to prevent them from running
    if (additionsSaveTimeoutRef.current) {
      clearTimeout(additionsSaveTimeoutRef.current);
      additionsSaveTimeoutRef.current = null;
    }
    if (deletionsSaveTimeoutRef.current) {
      clearTimeout(deletionsSaveTimeoutRef.current);
      deletionsSaveTimeoutRef.current = null;
    }
    
    setIsSavingAllComposition(true);
    setSaveAllStatus("saving");

    try {
      if (!window.sharePointAPI?.uploadFile) {
        console.error("sharePointAPI.uploadFile is not available");
        setSaveAllStatus("error");
        setIsSavingAllComposition(false);
        isManualSaveInProgressRef.current = false;
        return;
      }

      // First, trigger auto-save on all composition textboxes so the latest editor content
      // is pushed into localStorage. We read directly from localStorage, so state updates
      // from these functions won't affect the save operation.
      try {
        const allColumns = new Set<string>([
          ...Object.keys(additionsCompositionResponses),
          ...Object.keys(deletionsCompositionResponses),
          ...Object.keys(loadedAdditionsCompositionData || {}),
          ...Object.keys(loadedDeletionsCompositionData || {}),
        ]);
        allColumns.forEach((col) => {
          const autoSaveFunc = (window as any)[`autoSaveTextbox_${col}`];
          if (typeof autoSaveFunc === "function") {
            try {
              // These functions update localStorage, which is what we need
              // State updates they trigger will be blocked by isManualSaveInProgressRef
              autoSaveFunc();
            } catch (e) {
              console.warn(`Auto-save failed for composition textbox ${col}:`, e);
            }
          }
        });
        // Small delay to allow localStorage updates to settle
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (e) {
        console.warn("Error while triggering auto-save for composition textboxes", e);
      }

      // Collect all additions composition data (keyed by chart/column name)
      // Include all columns from loaded data, not just those in responses state
      const additionsData: Record<string, any> = {};
      const allAdditionsColumns = new Set<string>([
        ...Object.keys(additionsCompositionResponses),
        ...Object.keys(loadedAdditionsCompositionData || {}),
      ]);
      
      for (const columnName of allAdditionsColumns) {
        const response = additionsCompositionResponses[columnName] || {};
        // Always check localStorage first (highest priority - contains latest editor content)
        const textboxContent = localStorage.getItem(`textbox_${columnName}`);
        let textResponse = response.textResponse;
        
        // Prioritize localStorage content if it exists
        if (textboxContent && textboxContent.trim().length > 0) {
          textResponse = {
            html: textboxContent,
            plainText: textboxContent.replace(/<[^>]*>/g, ""),
          };
        } else if (!textResponse && response.textResponse) {
          // Fallback to state if localStorage is empty
          textResponse = response.textResponse;
        }
        
        // Build documents array (excluding textbox type) - same format as planning
        const fileDocuments = response.documents && Array.isArray(response.documents)
          ? response.documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
              name: doc.name,
              type: doc.type,
              url: doc.url,
              reference: doc.reference
            }))
          : [];
        
        additionsData[columnName] = {
          textResponse: textResponse || null,
          documents: fileDocuments.length > 0 ? fileDocuments : null,
        };
      }

      // Collect all deletions composition data (keyed by chart/column name)
      // Include all columns from loaded data, not just those in responses state
      const deletionsData: Record<string, any> = {};
      const allDeletionsColumns = new Set<string>([
        ...Object.keys(deletionsCompositionResponses),
        ...Object.keys(loadedDeletionsCompositionData || {}),
      ]);
      
      for (const columnName of allDeletionsColumns) {
        const response = deletionsCompositionResponses[columnName] || {};
        // Always check localStorage first (highest priority - contains latest editor content)
        const textboxContent = localStorage.getItem(`textbox_${columnName}`);
        let textResponse = response.textResponse;
        
        // Prioritize localStorage content if it exists
        if (textboxContent && textboxContent.trim().length > 0) {
          textResponse = {
            html: textboxContent,
            plainText: textboxContent.replace(/<[^>]*>/g, ""),
          };
        } else if (!textResponse && response.textResponse) {
          // Fallback to state if localStorage is empty
          textResponse = response.textResponse;
        }
        
        // Build documents array (excluding textbox type) - same format as planning
        const fileDocuments = response.documents && Array.isArray(response.documents)
          ? response.documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
              name: doc.name,
              type: doc.type,
              url: doc.url,
              reference: doc.reference
            }))
          : [];
        
        deletionsData[columnName] = {
          textResponse: textResponse || null,
          documents: fileDocuments.length > 0 ? fileDocuments : null,
        };
      }

      // Final JSON structure for Execution_IT_Substantive.json
      const fileData = {
        CompositionAnalysis: {
          Additions: additionsData,
          Deletions: deletionsData,
        },
      };

      const jsonString = JSON.stringify(fileData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const result = await window.sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: "Execution_IT_Substantive.json",
        referenceValue: "Execution IT Substantive",
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: ["Execution_IT_Substantive"],
      });

      if (result.success) {
        console.log(" Successfully saved all composition analysis data to Execution_IT_Substantive.json");
        setSaveAllStatus("saved");
        setTimeout(() => {
          setSaveAllStatus("idle");
        }, 3000);
      } else {
        console.error(" Failed to save composition analysis data:", result.error);
        setSaveAllStatus("error");
      }
    } catch (error) {
      console.error("Error saving all composition analysis data:", error);
      setSaveAllStatus("error");
    } finally {
      setIsSavingAllComposition(false);
      // Reset flag after a longer delay to ensure all state updates from autoSaveTextbox have settled
      // and to prevent auto-save from triggering immediately after manual save completes
      setTimeout(() => {
        isManualSaveInProgressRef.current = false;
      }, 2000); // Increased delay to 2 seconds to prevent immediate re-triggering
    }
  }, [additionsCompositionResponses, deletionsCompositionResponses, loadedAdditionsCompositionData, loadedDeletionsCompositionData]);

  const downloadAndDisplayTable = async () => {
    setIsLoadingTable(true);
    try {
      if (window.sharePointAPI?.downloadIaAdl) {
        const result = await window.sharePointAPI.downloadIaAdl();
        if (result.success && result.data) {
          setTableData(result.data);
          
          // Parse the JSON data structure: { Additions: {...}, Deletions: {...} }
          const data = result.data;
          
          // Extract table data from Additions and Deletions
          if (data.Additions) {
            // Load saved textResponse and documents if they exist (before filtering)
            if (data.Additions.textResponse) {
              setAdditionsTextResponse(data.Additions.textResponse);
            }
            if (data.Additions.documents && Array.isArray(data.Additions.documents)) {
              setAdditionsDocuments(data.Additions.documents.map((doc: any) => ({
                id: `doc_${doc.name || doc}`,
                type: doc.type || 'linked',
                name: doc.name || doc,
                url: doc.url,
                reference: doc.reference
              })));
            }
            
            // Convert Additions object to array format if needed
            // Filter out textResponse, documents, and table fields from display
            if (Array.isArray(data.Additions)) {
              // Filter out rows that are metadata fields
              const filteredAdditions = data.Additions.map((row: any) => {
                if (typeof row === 'object' && row !== null) {
                  const { textResponse, documents, table, ...displayRow } = row;
                  return displayRow;
                }
                return row;
              });
              setAdditionsTableData(filteredAdditions);
            } else if (typeof data.Additions === 'object') {
              // Convert object to array of rows, excluding metadata fields
              const additionsRows = Object.entries(data.Additions)
                .filter(([key]) => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                .map(([key, value]) => ({
                  Metric: key,
                  Value: value
                }));
              setAdditionsTableData(additionsRows);
            }
          }
          
          if (data.Deletions) {
            // Load saved textResponse and documents if they exist (before filtering)
            if (data.Deletions.textResponse) {
              setDeletionsTextResponse(data.Deletions.textResponse);
            }
            if (data.Deletions.documents && Array.isArray(data.Deletions.documents)) {
              setDeletionsDocuments(data.Deletions.documents.map((doc: any) => ({
                id: `doc_${doc.name || doc}`,
                type: doc.type || 'linked',
                name: doc.name || doc,
                url: doc.url,
                reference: doc.reference
              })));
            }
            
            // Convert Deletions object to array format if needed
            // Filter out textResponse, documents, and table fields from display
            if (Array.isArray(data.Deletions)) {
              // Filter out rows that are metadata fields
              const filteredDeletions = data.Deletions.map((row: any) => {
                if (typeof row === 'object' && row !== null) {
                  const { textResponse, documents, table, ...displayRow } = row;
                  return displayRow;
                }
                return row;
              });
              setDeletionsTableData(filteredDeletions);
            } else if (typeof data.Deletions === 'object') {
              // Convert object to array of rows, excluding metadata fields
              const deletionsRows = Object.entries(data.Deletions)
                .filter(([key]) => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                .map(([key, value]) => ({
                  Metric: key,
                  Value: value
                }));
              setDeletionsTableData(deletionsRows);
            }
          }
        }
      }
      
      // If no data from SharePoint, use dummy data for display
      if (additionsTableData.length === 0 && additionColumns.length > 0) {
        setAdditionsTableData(generateDummyTableData(additionColumns, 15));
      }
      if (deletionsTableData.length === 0 && deletionColumns.length > 0) {
        setDeletionsTableData(generateDummyTableData(deletionColumns, 10));
      }
      
      setShowAnalysisSelection(true);
    } catch (error) {
      console.error("Error downloading table data:", error);
      // Fallback to dummy data
      if (additionColumns.length > 0) {
        setAdditionsTableData(generateDummyTableData(additionColumns, 15));
      }
      if (deletionColumns.length > 0) {
        setDeletionsTableData(generateDummyTableData(deletionColumns, 10));
      }
      setShowAnalysisSelection(true);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const additionsColumnsForAnalysis = useMemo(() => {
    return additionColumns
      .filter(col => col.name !== additionsDateCol && col.name !== additionsAmountCol)
      .map(col => col.name);
  }, [additionColumns, additionsDateCol, additionsAmountCol]);

  const handleColumnToggle = (column: string) => {
    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  const generateAnalysis = () => {
    if (selectedColumns.size === 0 || additionsTableData.length === 0) return;
    
    setIsGeneratingAnalysis(true);
    const analyses: Record<string, any> = {};

    selectedColumns.forEach(column => {
      const valueCounts: Record<string, number> = {};
      const valueAmounts: Record<string, number> = {};
      const amountCol = additionsAmountCol;

      additionsTableData.forEach((row: any) => {
        const value = row[column];
        if (value !== null && value !== undefined && value !== '') {
          const key = String(value);
          valueCounts[key] = (valueCounts[key] || 0) + 1;
          if (amountCol && row[amountCol]) {
            const amount = parseFloat(String(row[amountCol]).replace(/,/g, '')) || 0;
            valueAmounts[key] = (valueAmounts[key] || 0) + amount;
          }
        }
      });

      const columnData = additionsTableData.map((row: any) => row[column]).filter((val: any) => val !== null && val !== undefined && val !== '');

      const chartData = Object.entries(valueCounts).map(([name, count]) => ({
        name,
        value: count,
        amount: valueAmounts[name] || 0
      }));

      const tableDataForColumn = Object.entries(valueCounts).map(([name, count]) => ({
        value: name,
        count,
        amount: valueAmounts[name] || 0,
        percentage: columnData.length > 0 ? ((count / columnData.length) * 100).toFixed(2) + '%' : '0%'
      })).sort((a, b) => b.count - a.count);

      analyses[column] = {
        chartData,
        tableData: tableDataForColumn,
        totalCount: columnData.length,
        uniqueValues: Object.keys(valueCounts).length
      };
    });

    setColumnAnalyses(analyses);
    setIsGeneratingAnalysis(false);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

  // Save tables with textResponse and documents to SharePoint
  const saveTables = useCallback(async () => {
    setSaveStatus('saving');
    setSaveError(null);
    
    try {
      // Trigger auto-save for both DocumentManagers to ensure latest content is captured
      const additionsAutoSave = (window as any)[`autoSaveTextbox_IA_ADL_Additions`];
      const deletionsAutoSave = (window as any)[`autoSaveTextbox_IA_ADL_Deletions`];
      
      if (additionsAutoSave && typeof additionsAutoSave === 'function') {
        additionsAutoSave();
      }
      if (deletionsAutoSave && typeof deletionsAutoSave === 'function') {
        deletionsAutoSave();
      }
      
      // Also capture content directly from contentEditable editors
      const additionsEditor = document.querySelector('[data-document-manager][data-item-id="IA_ADL_Additions"] [contenteditable="true"]') as HTMLElement;
      const deletionsEditor = document.querySelector('[data-document-manager][data-item-id="IA_ADL_Deletions"] [contenteditable="true"]') as HTMLElement;
      
      if (additionsEditor && additionsEditor.innerHTML.trim()) {
        const htmlContent = additionsEditor.innerHTML;
        localStorage.setItem('textbox_IA_ADL_Additions', htmlContent);
        console.log('[IA ADL] Saved additions textbox content directly from editor, length:', htmlContent.length);
      }
      
      if (deletionsEditor && deletionsEditor.innerHTML.trim()) {
        const htmlContent = deletionsEditor.innerHTML;
        localStorage.setItem('textbox_IA_ADL_Deletions', htmlContent);
        console.log('[IA ADL] Saved deletions textbox content directly from editor, length:', htmlContent.length);
      }
      
      // Wait a bit to ensure state updates complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check localStorage FIRST (highest priority) - same as planning phase
      const additionsTextboxContent = localStorage.getItem('textbox_IA_ADL_Additions');
      const deletionsTextboxContent = localStorage.getItem('textbox_IA_ADL_Deletions');
      
      const hasAdditionsTextboxContent = additionsTextboxContent && additionsTextboxContent.trim().length > 0;
      const hasDeletionsTextboxContent = deletionsTextboxContent && deletionsTextboxContent.trim().length > 0;
      
      const hasAdditionsTextResponse = additionsTextResponse && (
        (additionsTextResponse.html && additionsTextResponse.html.trim().length > 0) ||
        (additionsTextResponse.plainText && additionsTextResponse.plainText.trim().length > 0)
      );
      
      const hasDeletionsTextResponse = deletionsTextResponse && (
        (deletionsTextResponse.html && deletionsTextResponse.html.trim().length > 0) ||
        (deletionsTextResponse.plainText && deletionsTextResponse.plainText.trim().length > 0)
      );
      
      // Build textResponse for Additions - localStorage takes priority
      let additionsTextResponseData = null;
      if (hasAdditionsTextboxContent) {
        additionsTextResponseData = {
          html: additionsTextboxContent,
          plainText: additionsTextboxContent.replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
        };
        console.log('[IA ADL] Using additions textbox content from localStorage, length:', additionsTextboxContent.length);
      } else if (hasAdditionsTextResponse) {
        // Ensure it has both html and plainText
        if (additionsTextResponse.html && !additionsTextResponse.plainText) {
          additionsTextResponseData = {
            html: additionsTextResponse.html,
            plainText: additionsTextResponse.html.replace(/<[^>]*>/g, '')
          };
        } else if (additionsTextResponse.plainText && !additionsTextResponse.html) {
          additionsTextResponseData = {
            html: additionsTextResponse.plainText,
            plainText: additionsTextResponse.plainText
          };
        } else {
          additionsTextResponseData = additionsTextResponse;
        }
        console.log('[IA ADL] Using additions textResponse from state');
      }
      
      // Build textResponse for Deletions - localStorage takes priority
      let deletionsTextResponseData = null;
      if (hasDeletionsTextboxContent) {
        deletionsTextResponseData = {
          html: deletionsTextboxContent,
          plainText: deletionsTextboxContent.replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
        };
        console.log('[IA ADL] Using deletions textbox content from localStorage, length:', deletionsTextboxContent.length);
      } else if (hasDeletionsTextResponse) {
        // Ensure it has both html and plainText
        if (deletionsTextResponse.html && !deletionsTextResponse.plainText) {
          deletionsTextResponseData = {
            html: deletionsTextResponse.html,
            plainText: deletionsTextResponse.html.replace(/<[^>]*>/g, '')
          };
        } else if (deletionsTextResponse.plainText && !deletionsTextResponse.html) {
          deletionsTextResponseData = {
            html: deletionsTextResponse.plainText,
            plainText: deletionsTextResponse.plainText
          };
        } else {
          deletionsTextResponseData = deletionsTextResponse;
        }
        console.log('[IA ADL] Using deletions textResponse from state');
      }
      
      // Prepare the data structure to save
      const dataToSave: any = {
        Additions: {
          ...(tableData?.Additions && typeof tableData.Additions === 'object' && !Array.isArray(tableData.Additions) 
            ? Object.fromEntries(Object.entries(tableData.Additions).filter(([key]) => key !== 'textResponse' && key !== 'documents'))
            : {}),
          textResponse: additionsTextResponseData,
          documents: additionsDocuments.filter(doc => doc.type !== 'textbox').map(doc => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          }))
        },
        Deletions: {
          ...(tableData?.Deletions && typeof tableData.Deletions === 'object' && !Array.isArray(tableData.Deletions)
            ? Object.fromEntries(Object.entries(tableData.Deletions).filter(([key]) => key !== 'textResponse' && key !== 'documents'))
            : {}),
          textResponse: deletionsTextResponseData,
          documents: deletionsDocuments.filter(doc => doc.type !== 'textbox').map(doc => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          }))
        }
      };

      console.log('[IA ADL] Saving tables with textResponse:', {
        additionsHasTextResponse: !!additionsTextResponseData,
        additionsHtmlLength: additionsTextResponseData?.html?.length || 0,
        additionsPlainTextLength: additionsTextResponseData?.plainText?.length || 0,
        deletionsHasTextResponse: !!deletionsTextResponseData,
        deletionsHtmlLength: deletionsTextResponseData?.html?.length || 0,
        deletionsPlainTextLength: deletionsTextResponseData?.plainText?.length || 0
      });

      // Save to SharePoint via IPC
      if (window.sharePointAPI?.saveIaAdlTable) {
        const result = await window.sharePointAPI.saveIaAdlTable(dataToSave);
        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || 'Save failed');
        }
      } else {
        throw new Error('SharePoint API not available');
      }

      // Save inference question to Execution_IT_AL_Responses.json
      await new Promise(resolve => setTimeout(resolve, 500));
      try {
        const inferenceId = "IA-ADL-INFERENCE";
        const areaPrefix = "IT_AL";
        const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
        let inferenceDocumentNames: string[] = [];
        try {
          if ((window as any).sharePointAPI?.loadDocumentListBySection) {
            const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
            if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
              inferenceDocumentNames = dbResult.data
                .map((file: any) => file.name || '')
                .filter((name: string) => name && name.trim().length > 0);
            }
          }
        } catch (error) {
          console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
        }

        const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
        const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
        
        let inferenceTextResponseData = null;
        if (hasInferenceTextboxContent) {
          inferenceTextResponseData = {
            html: inferenceTextboxFromLocalStorage,
            plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (inferenceQuestion.textResponse) {
          inferenceTextResponseData = inferenceQuestion.textResponse;
        }

        const inferenceFileName = `Execution_${areaPrefix}_Responses.json`;
        let existingData: any = {};
        try {
          const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(inferenceFileName);
          if (downloadResult?.success && downloadResult?.data) {
            existingData = downloadResult.data;
          }
        } catch (error) {
          console.warn(`Failed to download existing ${inferenceFileName}, starting from empty`, error);
        }

        if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
          existingData = {};
        }

        if (!existingData.substantiveProcedures) {
          existingData.substantiveProcedures = {};
        }

        existingData.substantiveProcedures[inferenceId] = {
          selectedOption: "",
          textResponse: inferenceTextResponseData,
          documents: inferenceDocumentNames,
          table: null,
          lastUpdated: new Date().toISOString(),
          companyName: "ABC Private Limited", // You may want to load this dynamically
        };

        const jsonString = JSON.stringify(existingData, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

        const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
          fileContent: base64Content,
          fileName: inferenceFileName,
          referenceValue: `Execution ${areaPrefix} Substantive Responses`,
          folderName: "juggernaut",
          fyYear: getFY_YEAR(),
          sectionList: [`Execution_${areaPrefix}_Responses`],
        });

        if (!uploadResult || !uploadResult.success) {
          console.warn("Failed to save inference question:", uploadResult?.error);
        }
      } catch (error) {
        console.warn("Failed to save inference question:", error);
      }
    } catch (error) {
      console.error('Failed to save tables:', error);
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : 'Failed to save tables');
    }
  }, [tableData, additionsTextResponse, additionsDocuments, deletionsTextResponse, deletionsDocuments, inferenceQuestion]);

  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Get text from localStorage if available (DocumentManager auto-saves)
      const itemId = 'IA-ADL-INFERENCE';
      const autoSaveKey = `autoSaveTextbox_${itemId}`;
      const textboxKey = `textbox_${itemId}`;
      const savedText = localStorage.getItem(autoSaveKey) || localStorage.getItem(textboxKey) || inferenceQuestion.textResponse || '';

      const fileName = 'Execution_IT_AL_Responses.json';

      // Download existing file
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn('Failed to download existing file, starting fresh:', error);
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Wait a bit for localStorage sync
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Query db.json for linked/uploaded documents
      const section = `Execution_IT_AL_IA-ADL-INFERENCE`;
      let documents: any[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const docResult = await (window as any).sharePointAPI.loadDocumentListBySection(section);
          if (docResult?.success && Array.isArray(docResult.data)) {
            documents = docResult.data;
          }
        }
      } catch (error) {
        console.warn('Failed to load documents from db.json:', error);
      }

      // Update inference question data
      existingData.substantiveProcedures[itemId] = {
        selectedOption: 'textbox',
        textResponse: {
          html: savedText,
          plainText: savedText
        },
        documents: documents,
        lastUpdated: new Date().toISOString(),
        companyName: companyName
      };

      // Upload updated file
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }

      const result = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution IT AL Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_IT_AL_Responses`],
      });

      if (!result?.success) {
        throw new Error(result?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save inference question:', error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Additions & Deletions Listing - Intangible Assets</h2>
          <p className="text-gray-400">Select listings, map columns, and run analysis</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      {/* Additions Listing File Search */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Additions Listing File
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Additions Listing File"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {additionsFileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {additionsFileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {additionsFileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Additions File</Label>
                <Select value={additionsFile} onValueChange={setAdditionsFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundAdditionsFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for files - Additions"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search for files - Additions"])?.["Search for files - Additions"]}
                  </p>
                )}
              </div>
              {additionsFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadAdditionColumns} disabled={!additionsFile || isLoadingAddCols} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingAddCols ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Additions Columns"
                    )}
                  </Button>
                  {additionColumns.length > 0 && (
                    <span className="text-green-400 text-sm"> {additionColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {additionsFileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={additionsReferenceValue}
                    onChange={(e) => setAdditionsReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadAdditionsFile} disabled={additionsUploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {additionsUploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {additionsUploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {additionsUploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deletions Listing File Search */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Deletions Listing File
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Deletions Listing File"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {deletionsFileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {deletionsFileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {deletionsFileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Deletions File</Label>
                <Select value={deletionsFile} onValueChange={setDeletionsFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundDeletionsFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search for files - Deletions"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search for files - Deletions"])?.["Search for files - Deletions"]}
                  </p>
                )}
              </div>
              {deletionsFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadDeletionColumns} disabled={!deletionsFile || isLoadingDelCols} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingDelCols ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Deletions Columns"
                    )}
                  </Button>
                  {deletionColumns.length > 0 && (
                    <span className="text-green-400 text-sm"> {deletionColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {deletionsFileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={deletionsReferenceValue}
                    onChange={(e) => setDeletionsReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadDeletionsFile} disabled={deletionsUploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {deletionsUploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {deletionsUploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {deletionsUploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 2: Map Columns</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Map Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Additions Date</Label>
              <Select value={additionsDateCol} onValueChange={setAdditionsDateCol} disabled={additionColumns.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={additionColumns.length === 0 ? "Load columns first" : "Select column"} />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {additionColumns.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                try {
                  const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                  const lastUsed = mapping.additions?.dateColumn;
                  return lastUsed ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {lastUsed}
                    </p>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
            <div className="space-y-2">
              <Label className="text-white">Additions Amount</Label>
              <Select value={additionsAmountCol} onValueChange={setAdditionsAmountCol} disabled={additionColumns.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={additionColumns.length === 0 ? "Load columns first" : "Select column"} />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {additionColumns.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                try {
                  const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                  const lastUsed = mapping.additions?.amountColumn;
                  return lastUsed ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {lastUsed}
                    </p>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
            <div className="space-y-2">
              <Label className="text-white">Deletions Date</Label>
              <Select value={deletionsDateCol} onValueChange={setDeletionsDateCol} disabled={deletionColumns.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={deletionColumns.length === 0 ? "Load columns first" : "Select column"} />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {deletionColumns.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                try {
                  const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                  const lastUsed = mapping.deletions?.dateColumn;
                  return lastUsed ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {lastUsed}
                    </p>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
            <div className="space-y-2">
              <Label className="text-white">Deletions Amount</Label>
              <Select value={deletionsAmountCol} onValueChange={setDeletionsAmountCol} disabled={deletionColumns.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={deletionColumns.length === 0 ? "Load columns first" : "Select column"} />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {deletionColumns.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                try {
                  const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                  const lastUsed = mapping.deletions?.amountColumn;
                  return lastUsed ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {lastUsed}
                    </p>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Execute ADL
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute ADL"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={runADL} disabled={!canRun() || processingStatus === "running"} className="bg-green-600 hover:bg-green-700 text-white">
              {processingStatus === "running" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>) : ("Run ADL")}
            </Button>
            {processingStatus === "completed" && (<span className="text-green-400">Successful</span>)}
            {processingStatus === "error" && (<span className="text-red-400">Failed</span>)}
          </div>
          {fileUrl && (
            <div className="mt-4 p-3 bg-green-900/20 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">ADL completed! Results uploaded to SharePoint.</p>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline text-sm">View Results</a>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Analysis Results
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Analysis Results"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={downloadAndDisplayTable} 
            disabled={isLoadingTable}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isLoadingTable ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
            ) : (
              "Analysis Results"
            )}
          </Button>

          {(additionsTableData.length > 0 || deletionsTableData.length > 0) && (
            <div className="space-y-6">
              {/* Additions Table */}
              {additionsTableData.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Additions Listing</h3>
                  <div className="overflow-x-auto max-h-96 border border-white/20 rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          {Object.keys(additionsTableData[0] || {})
                            .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                            .map((key) => (
                            <TableHead key={key} className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm">
                              {key}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {additionsTableData.map((row: any, idx: number) => (
                          <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                            {Object.keys(additionsTableData[0] || {})
                              .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                              .map((key) => (
                              <TableCell key={key} className="border border-white/20 px-4 py-2 text-white text-sm">
                                {row[key] || '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4">
                    <DocumentManager
                      sectionKey="payroll"
                      itemId="IA_ADL_Additions"
                      itemQuestion="Additions Listing Documentation"
                      itemTextResponse={additionsTextResponse}
                      itemDocuments={additionsDocuments}
                      onDocumentsChange={(docs) => setAdditionsDocuments(docs)}
                      onTextResponseChange={(textResp) => setAdditionsTextResponse(textResp)}
                      disabled={false}
                    />
                  </div>
                </div>
              )}

              {/* Deletions Table */}
              {deletionsTableData.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Deletions Listing</h3>
                  <div className="overflow-x-auto max-h-96 border border-white/20 rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          {Object.keys(deletionsTableData[0] || {})
                            .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                            .map((key) => (
                            <TableHead key={key} className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm">
                              {key}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deletionsTableData.map((row: any, idx: number) => (
                          <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                            {Object.keys(deletionsTableData[0] || {})
                              .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                              .map((key) => (
                              <TableCell key={key} className="border border-white/20 px-4 py-2 text-white text-sm">
                                {row[key] || '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4">
                    <DocumentManager
                      sectionKey="payroll"
                      itemId="IA_ADL_Deletions"
                      itemQuestion="Deletions Listing Documentation"
                      itemTextResponse={deletionsTextResponse}
                      itemDocuments={deletionsDocuments}
                      onDocumentsChange={(docs) => setDeletionsDocuments(docs)}
                      onTextResponseChange={(textResp) => setDeletionsTextResponse(textResp)}
                      disabled={false}
                    />
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="text-sm text-white/60">
                  Save both tables with documentation to SharePoint
                </div>
                <Button
                  onClick={saveTables}
                  disabled={saveStatus === 'saving'}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {saveStatus === 'saving' ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Save Tables</>
                  )}
                </Button>
              </div>
              
              {/* Save Status Messages */}
              {saveStatus === 'saved' && (
                <div className="rounded-md bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-300">
                  Tables saved successfully to Execution_IT_ADL.json
                </div>
              )}
              {saveStatus === 'error' && saveError && (
                <div className="rounded-md bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-300">
                  Error saving tables: {saveError}
                </div>
              )}

              {/* Analysis Results */}
              {Object.keys(columnAnalyses).length > 0 && (
                <div className="space-y-8">
                  <h3 className="text-xl font-semibold text-white">Column Analysis Results</h3>
                  {Object.entries(columnAnalyses).map(([column, analysis]: [string, any]) => (
                    <div key={column} className="space-y-4 p-4 border border-white/20 rounded-lg bg-white/5">
                      <h4 className="text-lg font-semibold text-white">{column} Analysis</h4>
                      
                      {/* Pie Chart */}
                      {analysis.chartData.length > 0 && (
                        <div className="space-y-4">
                          <h5 className="text-md font-medium text-white/80">Distribution Chart</h5>
                          <div className="w-full h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPieChart>
                                <Pie
                                  data={analysis.chartData}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {analysis.chartData.map((entry: any, index: number) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </RechartsPieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Summary Table */}
                      <div className="space-y-4">
                        <h5 className="text-md font-medium text-white/80">Summary Table</h5>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-white/20">
                            <thead>
                              <tr className="bg-black/60">
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Value</th>
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Count</th>
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Percentage</th>
                                {additionsAmountCol && (
                                  <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Total Amount</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {analysis.tableData.map((row: any, idx: number) => (
                                <tr key={idx} className="hover:bg-white/5">
                                  <td className="border border-white/20 px-4 py-2 text-white">{row.value}</td>
                                  <td className="border border-white/20 px-4 py-2 text-white">{row.count}</td>
                                  <td className="border border-white/20 px-4 py-2 text-white">{row.percentage}</td>
                                  {additionsAmountCol && (
                                    <td className="border border-white/20 px-4 py-2 text-white">
                                      {row.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                    </td>
                                  )}
                                </tr>
                              ))}
                              <tr className="bg-black/40 font-semibold">
                                <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                <td className="border border-white/20 px-4 py-2 text-white">{analysis.totalCount}</td>
                                <td className="border border-white/20 px-4 py-2 text-white">100%</td>
                                {additionsAmountCol && (
                                  <td className="border border-white/20 px-4 py-2 text-white">
                                    {analysis.tableData.reduce((sum: number, row: any) => sum + row.amount, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Document Button */}
                      <div className="flex items-center gap-2 pt-2">
                        <FileText className="h-4 w-4 text-white/60" />
                        <span className="text-sm text-white/80">Document:</span>
                        <Button variant="outline" className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white">
                          Document
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Composition Analysis Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <PieChart className="h-5 w-5 text-purple-500" />
            Composition Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Additions Composition Analysis */}
          <div className="space-y-4 p-4 border border-white/20 rounded-lg bg-white/5">
            <h3 className="text-lg font-semibold text-white">Additions Composition Analysis</h3>
            <p className="text-sm text-white/60">Select columns from the Additions file to analyze composition by unique values and amounts.</p>
            
            <div className="space-y-3">
              <Label className="text-white">Select Columns for Analysis</Label>
              <div className="relative" ref={additionsDropdownRef}>
                <Button 
                  variant="outline" 
                  className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  onClick={() => {
                    setShowAdditionsCompositionDropdown(!showAdditionsCompositionDropdown);
                    if (showAdditionsCompositionDropdown) setAdditionsCompositionColumnsSearch("");
                  }}
                  disabled={additionColumns.length === 0}
                >
                  {additionsCompositionColumns.size > 0 
                    ? `${additionsCompositionColumns.size} column(s) selected` 
                    : additionColumns.length === 0 
                      ? "Load Additions Columns first" 
                      : "Select columns"}
                </Button>
                {showAdditionsCompositionDropdown && additionColumns.length > 0 && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-3 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={additionsCompositionColumnsSearch}
                        onChange={(e) => setAdditionsCompositionColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="space-y-2">
                      {additionColumns
                        .filter((col) => col.name.toLowerCase().includes(additionsCompositionColumnsSearch.toLowerCase()) || col.value.toLowerCase().includes(additionsCompositionColumnsSearch.toLowerCase()))
                        .map((col) => (
                          <div key={col.value} className="flex items-center space-x-2 px-2 py-1 hover:bg-white/5 rounded">
                            <Checkbox
                              id={`add-comp-${col.value}`}
                              checked={additionsCompositionColumns.has(col.value)}
                              onCheckedChange={() => handleAdditionsCompositionColumnToggle(col.value)}
                            />
                            <Label htmlFor={`add-comp-${col.value}`} className="text-white text-sm cursor-pointer">
                              {col.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {additionsCompositionColumns.size > 0 && (
                <div className="text-xs text-blue-300">
                  Selected: {Array.from(additionsCompositionColumns).join(', ')}
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-white">Amount Column</Label>
                <Select value={additionsCompositionAmountCol} onValueChange={setAdditionsCompositionAmountCol}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select amount column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {additionColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Run Additions Composition Analysis Button */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="text-sm text-white/60">
                Run composition analysis for additions
              </div>
              <Button
                onClick={runAdditionsCompositionAnalysis}
                disabled={
                  isRunningAdditionsCompositionAnalysis ||
                  additionsCompositionColumns.size === 0 ||
                  !additionsCompositionAmountCol
                }
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isRunningAdditionsCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Run Additions Composition Analysis</>
                )}
              </Button>
            </div>

            {/* Additions Composition Results */}
            {Object.keys(additionsCompositionResults).length > 0 && (
              <div className="space-y-6 mt-6">
                {Object.entries(additionsCompositionResults).map(([column, analysis]: [string, any]) => {
                  const columnResponse = additionsCompositionResponses[column] || {};
                  const columnTextResponse = columnResponse.textResponse;
                  const columnDocuments = columnResponse.documents || [];
                  
                  return (
                    <div key={column} className="space-y-4 p-4 border border-white/20 rounded-lg bg-black/20">
                      <h4 className="text-md font-semibold text-white">{column} Composition</h4>
                      {analysis.chartData && analysis.chartData.length > 0 && (
                        <div className="w-full h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={analysis.chartData}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {analysis.chartData.map((entry: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value: any, name: string, props: any) => {
                                  if (name === 'value') {
                                    return [`${value} items`, 'Count'];
                                  }
                                  return [value, name];
                                }}
                              />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                        {/* Document Manager for Additions Composition Analysis */}
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-white">Documentation</h5>
                          </div>
                          <DocumentManager
                          sectionKey="ia-additions-composition"
                          itemId={column}
                          itemQuestion={`${column} Composition Analysis`}
                          itemTextResponse={columnTextResponse}
                          itemDocuments={columnDocuments}
                          onTextResponseChange={(textResponse) => {
                            setAdditionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                textResponse
                              }
                            }));
                          }}
                          onDocumentsChange={(documents) => {
                            setAdditionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                documents
                              }
                            }));
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Deletions Composition Analysis */}
          <div className="space-y-4 p-4 border border-white/20 rounded-lg bg-white/5">
            <h3 className="text-lg font-semibold text-white">Deletions Composition Analysis</h3>
            <p className="text-sm text-white/60">Select columns from the Deletions file to analyze composition by unique values and amounts.</p>
            
            <div className="space-y-3">
              <Label className="text-white">Select Columns for Analysis</Label>
              <div className="relative" ref={deletionsDropdownRef}>
                <Button 
                  variant="outline" 
                  className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  onClick={() => {
                    setShowDeletionsCompositionDropdown(!showDeletionsCompositionDropdown);
                    if (showDeletionsCompositionDropdown) setDeletionsCompositionColumnsSearch("");
                  }}
                  disabled={deletionColumns.length === 0}
                >
                  {deletionsCompositionColumns.size > 0 
                    ? `${deletionsCompositionColumns.size} column(s) selected` 
                    : deletionColumns.length === 0 
                      ? "Load Deletions Columns first" 
                      : "Select columns"}
                </Button>
                {showDeletionsCompositionDropdown && deletionColumns.length > 0 && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-3 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={deletionsCompositionColumnsSearch}
                        onChange={(e) => setDeletionsCompositionColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="space-y-2">
                      {deletionColumns
                        .filter((col) => col.name.toLowerCase().includes(deletionsCompositionColumnsSearch.toLowerCase()) || col.value.toLowerCase().includes(deletionsCompositionColumnsSearch.toLowerCase()))
                        .map((col) => (
                          <div key={col.value} className="flex items-center space-x-2 px-2 py-1 hover:bg-white/5 rounded">
                            <Checkbox
                              id={`del-comp-${col.value}`}
                              checked={deletionsCompositionColumns.has(col.value)}
                              onCheckedChange={() => handleDeletionsCompositionColumnToggle(col.value)}
                            />
                            <Label htmlFor={`del-comp-${col.value}`} className="text-white text-sm cursor-pointer">
                              {col.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {deletionsCompositionColumns.size > 0 && (
                <div className="text-xs text-blue-300">
                  Selected: {Array.from(deletionsCompositionColumns).join(', ')}
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-white">Amount Column</Label>
                <Select value={deletionsCompositionAmountCol} onValueChange={setDeletionsCompositionAmountCol}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select amount column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {deletionColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Run Deletions Composition Analysis Button */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="text-sm text-white/60">
                Run composition analysis for deletions
              </div>
              <Button
                onClick={runDeletionsCompositionAnalysis}
                disabled={
                  isRunningDeletionsCompositionAnalysis ||
                  deletionsCompositionColumns.size === 0 ||
                  !deletionsCompositionAmountCol
                }
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isRunningDeletionsCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Run Deletions Composition Analysis</>
                )}
              </Button>
            </div>

            {/* Deletions Composition Results */}
            {Object.keys(deletionsCompositionResults).length > 0 && (
              <div className="space-y-6 mt-6">
                {Object.entries(deletionsCompositionResults).map(([column, analysis]: [string, any]) => {
                  const columnResponse = deletionsCompositionResponses[column] || {};
                  const columnTextResponse = columnResponse.textResponse;
                  const columnDocuments = columnResponse.documents || [];
                  
                  return (
                    <div key={column} className="space-y-4 p-4 border border-white/20 rounded-lg bg-black/20">
                      <h4 className="text-md font-semibold text-white">{column} Composition</h4>
                      {analysis.chartData && analysis.chartData.length > 0 && (
                        <div className="w-full h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={analysis.chartData}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {analysis.chartData.map((entry: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value: any, name: string, props: any) => {
                                  if (name === 'value') {
                                    return [`${value} items`, 'Count'];
                                  }
                                  return [value, name];
                                }}
                              />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                        {/* Document Manager for Deletions Composition Analysis */}
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-white">Documentation</h5>
                          </div>
                          <DocumentManager
                          sectionKey="ia-deletions-composition"
                          itemId={column}
                          itemQuestion={`${column} Composition Analysis`}
                          itemTextResponse={columnTextResponse}
                          itemDocuments={columnDocuments}
                          onTextResponseChange={(textResponse) => {
                            setDeletionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                textResponse
                              }
                            }));
                          }}
                          onDocumentsChange={(documents) => {
                            setDeletionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                documents
                              }
                            }));
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Load Analysis Section */}
          <div className="pt-6 border-t border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-white/60">
                Load composition analysis results from SharePoint
              </div>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <Button 
                onClick={loadCompositionAnalysis}
                disabled={isLoadingCompositionAnalysis}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isLoadingCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Load Additions Analysis</>
                )}
              </Button>
              
              <Button 
                onClick={loadDeletionsCompositionAnalysis}
                disabled={isLoadingDeletionsAnalysis}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isLoadingDeletionsAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Load Deletions Analysis</>
                )}
              </Button>
            </div>

          {/* Display Loaded Results */}
          {(Object.keys(loadedAdditionsCompositionData).length > 0 || Object.keys(loadedDeletionsCompositionData).length > 0) ? (
            <div className="space-y-8 mt-6">
              {/* Additions Results */}
              {Object.keys(loadedAdditionsCompositionData).length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-white">Additions Composition Analysis</h3>
                  {Object.entries(loadedAdditionsCompositionData).map(([columnName, columnData]: [string, any]) => {
                    // Handle both array format and object format with data/textResponse/documents
                    let dataArray: any[] = [];
                    const columnDataType = Array.isArray(columnData) ? 'array' : typeof columnData;
                    const columnDataKeys = typeof columnData === 'object' && columnData !== null ? Object.keys(columnData).join(', ') : 'N/A';
                    
                    if (Array.isArray(columnData)) {
                      dataArray = columnData;
                    } else if (columnData && typeof columnData === 'object' && Array.isArray(columnData.data)) {
                      dataArray = columnData.data;
                    } else {
                      // Log to debug info instead of console
                      setDebugInfo(prev => ({
                        ...prev,
                        additions: {
                          ...prev.additions,
                          step: `Render: Skipping ${columnName}`,
                          details: `Invalid format. Type: ${columnDataType}, Keys: ${columnDataKeys}, Value: ${JSON.stringify(columnData).substring(0, 200)}`
                        }
                      }));
                      return null;
                    }
                    
                    const totalAmount = dataArray.reduce((sum: number, item: any) => sum + Math.abs(item.amount || 0), 0);
                    const chartData = dataArray.map((item: any) => ({
                      name: String(item.label || ''),
                      value: Math.abs(Number(item.amount || 0))
                    })).filter(item => item.value !== 0);
                    
                    console.log(`Rendering chart for ${columnName}:`, { chartData, totalAmount });

                    const columnResponse = additionsCompositionResponses[columnName] || {};
                    // Get textResponse and documents from loaded data or state
                    const loadedColumnData = Array.isArray(columnData) ? null : columnData;
                    const columnTextResponse = loadedColumnData?.textResponse || columnResponse.textResponse;
                    const columnDocuments = loadedColumnData?.documents || columnResponse.documents || [];
                    
                    return (
                      <div key={columnName} className="p-4 border border-white/20 rounded-lg bg-white/5">
                        <h4 className="text-lg font-semibold text-white mb-4">{columnName}</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Pie Chart */}
                          {chartData && chartData.length > 0 ? (
                            <div className="w-full" style={{ minHeight: '320px', height: '320px' }}>
                              <ResponsiveContainer width="100%" height={320}>
                                <RechartsPieChart>
                                  <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {chartData.map((entry: any, index: number) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip 
                                    formatter={(value: any) => [
                                      `${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
                                      'Amount'
                                    ]}
                                  />
                                  <Legend />
                                </RechartsPieChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="w-full h-80 flex items-center justify-center text-white/60 border border-white/10 rounded">
                              No chart data available (chartData length: {chartData?.length || 0})
                            </div>
                          )}

                          {/* Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-white/20">
                              <thead>
                                <tr className="bg-black/60">
                                  <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Label</th>
                                  <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Amount</th>
                                  <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Percentage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dataArray.map((item: any, idx: number) => {
                                  const amount = Math.abs(item.amount || 0);
                                  const percentage = totalAmount > 0 ? (amount / totalAmount * 100).toFixed(2) : '0.00';
                                  return (
                                    <tr key={idx} className="hover:bg-white/5">
                                      <td className="border border-white/20 px-4 py-2 text-white">{String(item.label || '')}</td>
                                      <td className="border border-white/20 px-4 py-2 text-white text-right">
                                        {Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                      </td>
                                      <td className="border border-white/20 px-4 py-2 text-white text-right">{percentage}%</td>
                                    </tr>
                                  );
                                })}
                                <tr className="bg-black/40 font-semibold">
                                  <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                  <td className="border border-white/20 px-4 py-2 text-white text-right">
                                    {totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="border border-white/20 px-4 py-2 text-white text-right">100%</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {/* Document Manager for Additions Composition Analysis */}
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-white">Documentation</h5>
                          </div>
                          <DocumentManager
                            sectionKey="ia-additions-composition"
                            itemId={columnName}
                            itemQuestion={`${columnName} Composition Analysis`}
                            itemTextResponse={columnTextResponse}
                            itemDocuments={columnDocuments}
                            onTextResponseChange={(textResponse) => {
                              setAdditionsCompositionResponses(prev => ({
                                ...prev,
                                [columnName]: {
                                  ...prev[columnName],
                                  textResponse
                                }
                              }));
                            }}
                            onDocumentsChange={(documents) => {
                              setAdditionsCompositionResponses(prev => ({
                                ...prev,
                                [columnName]: {
                                  ...prev[columnName],
                                  documents
                                }
                              }));
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Deletions Results */}
              {Object.keys(loadedDeletionsCompositionData).length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-white">Deletions Composition Analysis</h3>
                  {Object.entries(loadedDeletionsCompositionData).map(([columnName, columnData]: [string, any]) => {
                    // Handle both array format and object format with data/textResponse/documents
                    let dataArray: any[] = [];
                    if (Array.isArray(columnData)) {
                      dataArray = columnData;
                    } else if (columnData && typeof columnData === 'object' && Array.isArray(columnData.data)) {
                      dataArray = columnData.data;
                    } else {
                      return null;
                    }
                    
                    const totalAmount = dataArray.reduce((sum: number, item: any) => sum + Math.abs(item.amount || 0), 0);
                    const chartData = dataArray.map((item: any) => ({
                      name: String(item.label || ''),
                      value: Math.abs(Number(item.amount || 0))
                    })).filter(item => item.value !== 0);

                    const columnResponse = deletionsCompositionResponses[columnName] || {};
                    // Get textResponse and documents from loaded data or state
                    const loadedColumnData = Array.isArray(columnData) ? null : columnData;
                    const columnTextResponse = loadedColumnData?.textResponse || columnResponse.textResponse;
                    const columnDocuments = loadedColumnData?.documents || columnResponse.documents || [];
                    
                    return (
                      <div key={columnName} className="p-4 border border-white/20 rounded-lg bg-white/5">
                        <h4 className="text-lg font-semibold text-white mb-4">{columnName}</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Pie Chart */}
                          {chartData && chartData.length > 0 ? (
                            <div className="w-full" style={{ minHeight: '320px', height: '320px' }}>
                              <ResponsiveContainer width="100%" height={320}>
                                <RechartsPieChart>
                                  <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {chartData.map((entry: any, index: number) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip 
                                    formatter={(value: any) => [
                                      `${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
                                      'Amount'
                                    ]}
                                  />
                                  <Legend />
                                </RechartsPieChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="w-full h-80 flex items-center justify-center text-white/60 border border-white/10 rounded">
                              No chart data available
                            </div>
                          )}

                          {/* Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-white/20">
                              <thead>
                                <tr className="bg-black/60">
                                  <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Label</th>
                                  <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Amount</th>
                                  <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Percentage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dataArray.map((item: any, idx: number) => {
                                  const amount = Math.abs(item.amount || 0);
                                  const percentage = totalAmount > 0 ? (amount / totalAmount * 100).toFixed(2) : '0.00';
                                  return (
                                    <tr key={idx} className="hover:bg-white/5">
                                      <td className="border border-white/20 px-4 py-2 text-white">{String(item.label || '')}</td>
                                      <td className="border border-white/20 px-4 py-2 text-white text-right">
                                        {Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                      </td>
                                      <td className="border border-white/20 px-4 py-2 text-white text-right">{percentage}%</td>
                                    </tr>
                                  );
                                })}
                                <tr className="bg-black/40 font-semibold">
                                  <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                  <td className="border border-white/20 px-4 py-2 text-white text-right">
                                    {totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="border border-white/20 px-4 py-2 text-white text-right">100%</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {/* Document Manager for Deletions Composition Analysis */}
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-white">Documentation</h5>
                          </div>
                          <DocumentManager
                            sectionKey="ia-deletions-composition"
                            itemId={columnName}
                            itemQuestion={`${columnName} Composition Analysis`}
                            itemTextResponse={columnTextResponse}
                            itemDocuments={columnDocuments}
                            onTextResponseChange={(textResponse) => {
                              setDeletionsCompositionResponses(prev => ({
                                ...prev,
                                [columnName]: {
                                  ...prev[columnName],
                                  textResponse
                                }
                              }));
                            }}
                            onDocumentsChange={(documents) => {
                              setDeletionsCompositionResponses(prev => ({
                                ...prev,
                                [columnName]: {
                                  ...prev[columnName],
                                  documents
                                }
                              }));
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}  
                </div>
              )}
              
              {/* Save All Button */}
              {(Object.keys(loadedAdditionsCompositionData).length > 0 || Object.keys(loadedDeletionsCompositionData).length > 0) && (
                <div className="pt-6 border-t border-white/10 mt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-white/60">
                      Save all composition analysis data to SharePoint
                    </div>
                    <div className="flex items-center gap-3">
                      {saveAllStatus === 'saved' && (
                        <span className="text-sm text-green-400">Saved successfully!</span>
                      )}
                      {saveAllStatus === 'error' && (
                        <span className="text-sm text-red-400">Error saving</span>
                      )}
                      <Button
                        onClick={saveAllCompositionAnalysis}
                        disabled={isSavingAllComposition}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {isSavingAllComposition ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="h-4 w-4 mr-2" /> Save All</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-white/60 mt-4">
              No data loaded. Click "Load Additions Analysis" or "Load Deletions Analysis" to download results from SharePoint.
            </div>
          )}
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Additions Listing File")]: "Step 1: Search for Additions Listing File",
              [getSectionQuestionId("Step 1: Search for Deletions Listing File")]: "Step 1: Search for Deletions Listing File",
              [getSectionQuestionId("Step 2: Map Columns")]: "Step 2: Map Columns",
              [getSectionQuestionId("Execute ADL")]: "Execute ADL",
              [getSectionQuestionId("Analysis Results")]: "Analysis Results",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
          workspace={workspaceName}
          subPhase={procedureName}
        />
      )}

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="ia-additions-deletions"
            itemId="IA-ADL-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>
    </div>
  );
}




